'use client';

import React from 'react';
import styled from 'styled-components';

export interface UptimeBarDisplayProps {
  /** Hourly data array: 1=up (green), 0=down (red), 2/null=no data (gray) */
  hourlyData: number[] | null;
  /** Optional uptime percentage to display */
  uptimePercentage?: string | number;
  /** Show percentage above the bar */
  showPercentage?: boolean;
  /** Variant: 'inline' for tables (horizontal), 'stacked' for cards (vertical) */
  variant?: 'inline' | 'stacked';
  /** Custom class name */
  className?: string;
}

const Container = styled.div<{ $variant: 'inline' | 'stacked' }>`
  display: flex;
  flex-direction: ${props => props.$variant === 'stacked' ? 'column' : 'row'};
  gap: ${props => props.$variant === 'stacked' ? '5px' : '8px'};
  align-items: ${props => props.$variant === 'stacked' ? 'flex-end' : 'center'};
  justify-content: ${props => props.$variant === 'stacked' ? 'center' : 'flex-start'};
  min-width: ${props => props.$variant === 'stacked' ? '220px' : 'auto'};
  width: ${props => props.$variant === 'stacked' ? '100%' : 'auto'};
`;

const Percentage = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: ${props => props.theme.colors.text.primary};
  white-space: nowrap;
`;

const BarContainer = styled.div<{ $variant: 'inline' | 'stacked' }>`
  width: ${props => props.$variant === 'stacked' ? '100%' : '192px'};
  height: 10px;
  overflow: hidden;
  display: flex;
`;

const BarSegment = styled.div<{ $color: string; $variant: 'inline' | 'stacked' }>`
  flex: 1;
  background-color: ${props => props.$color};
  margin: 0 1px;
  min-width: ${props => props.$variant === 'stacked' ? '8px' : 'auto'};
  
  &:first-child {
    margin-left: 0;
  }
  
  &:last-child {
    margin-right: 0;
  }
`;

/**
 * UptimeBarDisplay Component
 * 
 * A compact uptime bar visualization for displaying hourly uptime data.
 * Supports two variants:
 * - 'inline': For use in tables (percentage above bar, vertically stacked)
 * - 'stacked': For use in cards (percentage beside bar, horizontally aligned)
 * 
 * Color coding (matches monitors/[id] page implementation):
 * - Green (#009951): Up/Success (value = 1)
 * - Red (#EC221F): Down/Failure (value = 0)
 * - Gray (#D9D9DC): No data (value = 2 or null/undefined/other)
 */
export const UptimeBarDisplay: React.FC<UptimeBarDisplayProps> = ({
  hourlyData,
  uptimePercentage,
  showPercentage = true,
  variant = 'inline',
  className,
}) => {
  // Generate bar segments based on hourly data (exact implementation from monitors/[id] page)
  const generateBarSegments = () => {
    const data = (hourlyData && hourlyData?.length > 0
      ? hourlyData
      : Array(24).fill(2)) as Array<number>;

    return data.map((value, index) => {
      // API returns: 1 = up/success, 0 = down/fail, null/undefined/other = no data
      let color = '#D9D9DC'; // default for no data
      if (value === 1) {
        color = '#009951'; // green for up
      } else if (value === 0) {
        color = '#EC221F'; // red for down
      }
      return <BarSegment key={index} $color={color} $variant={variant} />;
    });
  };

  return (
    <Container $variant={variant} className={className}>
      {showPercentage && uptimePercentage !== undefined && (
        <Percentage>{uptimePercentage}{typeof uptimePercentage === 'number' ? '%' : ''}</Percentage>
      )}
      <BarContainer $variant={variant}>
        {generateBarSegments()}
      </BarContainer>
    </Container>
  );
};

export default UptimeBarDisplay;

